import axios from 'axios';

// Criar instância do axios com configurações padrão
const api = axios.create({
  baseURL: 'https://trajetus.net',
  timeout: 30000, // Aumentado de 10s para 30s
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação e normalizar URL
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Normalizar URL para evitar duplicação de /api quando baseURL já contém /api
    try {
      const base = api.defaults.baseURL || '';
      const url = config.url || '';
      const baseEndsWithApi = /\/?api\/?$/i.test(base);
      const urlStartsWithApi = /^\/?api(\/|$)/i.test(url);
      if (baseEndsWithApi && urlStartsWithApi) {
        // Remove um prefixo /api da URL
        config.url = url.replace(/^\/?api(\/)?/i, '/');
      }
    } catch (_) { /* noop */ }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => {
    // Retornar o objeto response completo para manter compatibilidade
    return response;
  },
  (error) => {
    console.error('🔍 [DEBUG] Erro da API:', error);
    
    // Tratar erro de autenticação
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    // Tratar erro 403 (Forbidden) - pode ser CORS ou permissão
    if (error.response?.status === 403) {
      console.warn('⚠️ [DEBUG] Erro 403 - Possível problema de CORS ou permissão');
    }
    
    // Retornar erro formatado
    return Promise.reject(error);
  }
);

// =====================================================
// SERVIÇOS DE AUTENTICAÇÃO
// =====================================================
export const authService = {
  async login(email, senha) {
    console.log('🔐 [DEBUG] Tentando login com:', { email, senha });
    
    try {
      const response = await api.post('/api/auth/login', { email, senha });
      console.log('✅ [DEBUG] Resposta do backend:', response);
      
      const responseData = response.data;
      
      if (responseData && responseData.success && responseData.data && responseData.data.token) {
        // ✅ NÃO SALVAR AUTOMATICAMENTE - deixar o AuthContext decidir
        console.log('🎉 [DEBUG] Login bem-sucedido');
        return { success: true, data: responseData.data };
      } else {
        console.log('❌ [DEBUG] Resposta sem token válido:', responseData);
        return { success: false, message: responseData?.message || 'Resposta inválida do servidor' };
      }
    } catch (error) {
      console.error('💥 [DEBUG] Erro no login:', error);
      
      if (error.response && error.response.data) {
        return { success: false, message: error.response.data.message || 'Erro no servidor' };
      }
      
      throw error;
    }
  },

  async checkStatus(email) {
    try {
      const resp = await api.get(`/api/auth/check-status/${encodeURIComponent(email)}`);
      return resp.data; // { success, data: { email, ativo } }
    } catch (error) {
      if (error.response && error.response.data) return error.response.data;
      throw error;
    }
  },

  async logout() {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  },

  async verificarToken() {
    return await api.get('/api/auth/verify');
  },

  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  async forgotPassword(email) {
    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) return error.response.data;
      throw error;
    }
  },

  async resetPassword(token, novaSenha) {
    try {
      const response = await api.post('/api/auth/reset-password', { token, novaSenha });
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) return error.response.data;
      throw error;
    }
  },

  async enviarCodigo2FAEmail(email, codigo) {
    try {
      const response = await api.post('/api/auth/enviar-codigo-2fa-email', { email, codigo });
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) return error.response.data;
      throw error;
    }
  }
};

// =====================================================
// SERVIÇOS STRIPE
// =====================================================
export const stripeService = {
  async createCheckoutSession() {
    const priceId = process.env.REACT_APP_STRIPE_PRICE_ID;
    if (!priceId) {
      throw new Error('REACT_APP_STRIPE_PRICE_ID não configurado');
    }
    return await api.post('/api/stripe/checkout-session', { priceId });
  },

  async createPortalSession() {
    return await api.post('/api/stripe/portal-session', {});
  },

  async getMinhaAssinatura() {
    return await api.get('/api/stripe/me');
  }
};

// =====================================================
// SERVIÇOS DE CLIENTES
// =====================================================
export const clienteService = {
  async listar(filtros = {}) {
    const params = new URLSearchParams();
    
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    return await api.get(`/api/clientes?${params.toString()}`);
  },

  async listarPorUsuario(id_usuario) {
    return await api.get(`/api/clientes/usuario/${id_usuario}`);
  },

  async buscarPorId(id) {
    return await api.get(`/api/clientes/${id}`);
  },

  async criar(dados) {
    return await api.post('/api/clientes', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/clientes/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/clientes/${id}`);
  },

  async uploadFoto(id, arquivo) {
    const formData = new FormData();
    formData.append('foto', arquivo);
    
    return await api.post(`/api/clientes/${id}/foto`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
};

// =====================================================
// SERVIÇOS DE EXPERIÊNCIAS
// =====================================================
export const experienciaService = {
  async listar(filtros = {}) {
    const params = new URLSearchParams();
    
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    return await api.get(`/api/experiencia/admin/listar?${params.toString()}`);
  },

  async buscarPorId(id) {
    return await api.get(`/api/experiencia/admin/${id}`);
  },

  async buscarPorCliente(clienteId) {
    return await api.get(`/api/experiencias/cliente/${clienteId}`);
  },

  async criar(dados) {
    return await api.post('/api/experiencias', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/experiencias/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/experiencias/${id}`);
  }
};

// =====================================================
// SERVIÇOS DE AGENDAMENTOS
// =====================================================
export const agendamentoService = {
  async listar(filtros = {}) {
    const params = new URLSearchParams();
    
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    return await api.get(`/api/agendamentos?${params.toString()}`);
  },

  async buscarPorId(id) {
    return await api.get(`/api/agendamentos/${id}`);
  },

  async buscarPorPeriodo(dataInicio, dataFim) {
    return await api.get(`/api/agendamentos/periodo?inicio=${dataInicio}&fim=${dataFim}`);
  },

  async criar(dados) {
    return await api.post('/api/agendamentos', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/agendamentos/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/agendamentos/${id}`);
  },

  async alterarStatus(id, status) {
    return await api.patch(`/api/agendamentos/${id}/status`, { status });
  },

  async gerarAgenda(mes, ano) {
    return await api.post('/api/agendamentos/gerar-agenda', { mes, ano });
  },

  // Novos endpoints para GerarAgenda
  async buscarClientesComHorarios(idUsuario) {
    try {
      const response = await api.get(`/api/agendamentos/clientes-horarios/${idUsuario}`);
      return response.data; // Retornar response.data diretamente
    } catch (error) {
      console.error('Erro no buscarClientesComHorarios:', error);
      throw error;
    }
  },

  async buscarAgendamentosPorMes(mes, ano, idUsuario) {
    return await api.get(`/api/agendamentos/mes/${mes}/ano/${ano}/usuario/${idUsuario}`);
  },

  async deletarAgendamentosMes(mes, ano, idUsuario) {
    return await api.delete(`/api/agendamentos/mes/${mes}/ano/${ano}/usuario/${idUsuario}`);
  },

  async criarAgendamentosMultiplos(agendamentos, idUsuario) {
    try {
      const response = await api.post('/api/agendamentos/criar-multiplos', { agendamentos, id_usuario: idUsuario });
      return response.data; // Retornar response.data diretamente
    } catch (error) {
      console.error('Erro no criarAgendamentosMultiplos:', error);
      throw error;
    }
  },

  // Atualizar data de agendamento (para drag-and-drop)
  async atualizarDataAgendamento(id, novaData) {
    return await api.patch(`/api/agendamentos/${id}/data`, { nova_data: novaData });
  }
};

// =====================================================
// SERVIÇOS DE EVOLUÇÃO
// =====================================================
export const evolucaoService = {
  async listar(filtros = {}) {
    const params = new URLSearchParams();
    
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    return await api.get(`/api/evolucoes?${params.toString()}`);
  },

  async buscarPorId(id) {
    return await api.get(`/api/evolucoes/${id}`);
  },

  async buscarPorCliente(clienteId) {
    return await api.get(`/api/evolucoes/cliente/${clienteId}`);
  },

  async buscarPorUsuario(userId) {
    console.log('🔍 [API] Chamando buscarPorUsuario para userId:', userId);
    const url = `/api/evolucoes/usuario/${userId}`;
    console.log('🔍 [API] URL completa:', url);
    try {
      const response = await api.get(url);
      console.log('🔍 [API] Resposta recebida:', response);
      return response;
    } catch (error) {
      console.error('❌ [API] Erro na requisição:', error);
      throw error;
    }
  },

  async criar(dados) {
    return await api.post('/api/evolucoes', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/evolucoes/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/evolucoes/${id}`);
  },

  async estatisticas(filtros = {}) {
    const params = new URLSearchParams();
    
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    return await api.get(`/api/evolucoes/estatisticas?${params.toString()}`);
  }
};

// =====================================================
// SERVIÇOS DE DESPESAS
// =====================================================
export const despesaService = {
  async listar() {
    return await api.get('/api/despesas');
  },

  async buscarPorUsuario(id_usuario) {
    return await api.get(`/api/despesas/usuario/${id_usuario}`);
  },

  async buscarRecentesPorUsuario(id_usuario) {
    return await api.get(`/api/despesas/usuario/${id_usuario}/recentes`);
  },

  async buscarPorId(id) {
    return await api.get(`/api/despesas/${id}`);
  },

  async criar(dados) {
    return await api.post('/api/despesas', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/despesas/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/despesas/${id}`);
  },

  async uploadComprovante(id, arquivo) {
    const formData = new FormData();
    formData.append('comprovante', arquivo);
    
    return await api.post(`/api/despesas/${id}/comprovante`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
};

// =====================================================
// SERVIÇOS DE TIPOS DE DESPESAS
// =====================================================
export const tipoDespesaService = {
  async listar() {
    return await api.get('/api/tipos-despesa');
  },

  async listarPorUsuario(id_usuario) {
    return await api.get(`/api/tipos-despesa/usuario/${id_usuario}`);
  },

  async criar(dados) {
    return await api.post('/api/tipos-despesa', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/tipos-despesa/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/tipos-despesa/${id}`);
  }
};

// SERVIÇOS DE USUÁRIOS E GRUPOS
// =====================================================
export const usuarioService = {
  async listar() {
    return await api.get('/api/usuarios');
  },

  async buscarPorId(id) {
    return await api.get(`/api/usuarios/${id}`);
  },

  async buscarPorEmail(email) {
    return await api.get(`/api/usuarios/email/${email}`);
  },

  async criar(dados) {
    return await api.post('/api/usuarios', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/usuarios/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/usuarios/${id}`);
  },

  async uploadFoto(id, arquivo) {
    const formData = new FormData();
    formData.append('foto', arquivo);
    return await api.post(`/api/usuarios/${id}/foto`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  async salvarFotoPorUrl(id, url) {
    return await api.post(`/api/usuarios/${id}/foto-from-url`, { url });
  },

  async alterarSenha(id, senhaAtual, novaSenha) {
    return await api.put(`/api/usuarios/${id}/senha`, {
      senhaAtual,
      novaSenha
    });
  },

  async buscarPorFiltros(filtros) {
    return await api.get('/api/usuarios/filtros/buscar', { params: filtros });
  },

  async obterEstatisticas() {
    return await api.get('/api/usuarios/estatisticas');
  },

  async enviarCodigo2FA(usuarioId, codigo) {
    console.log('🚀 [API] Iniciando enviarCodigo2FA - usuarioId:', usuarioId, 'codigo:', codigo);
    console.log('🚀 [API] URL da requisição:', `https://trajetus.net/usuarios/${usuarioId}/enviar-codigo-2fa`);
    
    try {
      const response = await api.post(`/api/usuarios/${usuarioId}/enviar-codigo-2fa`, { codigo });
      console.log('✅ [API] Resposta recebida do backend:', response);
      console.log('✅ [API] Status da resposta:', response.status);
      console.log('✅ [API] Dados da resposta:', response.data);
      return response;
    } catch (error) {
      console.error('❌ [API] Erro na requisição enviarCodigo2FA:', error);
      console.error('❌ [API] Status do erro:', error.response?.status);
      console.error('❌ [API] Dados do erro:', error.response?.data);
      throw error;
    }
  },

  async verificarCodigo2FA(usuarioId, codigo) {
    return await api.post(`/api/usuarios/${usuarioId}/verificar-codigo-2fa`, { codigo });
  }
};

export const grupoUsuarioService = {
  async listar() {
    return await api.get('/api/grupos-usuarios');
  },

  async buscarPorId(id) {
    return await api.get(`/api/grupos-usuarios/${id}`);
  },

  async criar(dados) {
    return await api.post('/api/grupos-usuarios', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/grupos-usuarios/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/grupos-usuarios/${id}`);
  }
};

export const nivelAcessoService = {
  async listar() {
    return await api.get('/api/niveis-acesso');
  },

  async buscarPorId(id) {
    return await api.get(`/api/niveis-acesso/${id}`);
  },

  async buscarPermissoesPorUsuario(userId) {
    console.log('🔍 [API] Chamando buscarPermissoesPorUsuario para userId:', userId);
    const url = `/api/niveis-acesso/usuario/${userId}/permissoes`;
    console.log('🔍 [API] URL completa:', url);
    try {
      const response = await api.get(url);
      console.log('🔍 [API] Resposta recebida:', response);
      return response;
    } catch (error) {
      console.error('❌ [API] Erro na requisição:', error);
      throw error;
    }
  },

  async criar(dados) {
    return await api.post('/api/niveis-acesso', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/niveis-acesso/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/niveis-acesso/${id}`);
  }
};

// =====================================================
// SERVIÇOS DE RELATÓRIOS E DASHBOARD
// =====================================================
export const dashboardService = {
  async obterEstatisticas() {
    return await api.get('/api/dashboard/stats');
  },

  async obterAgendamentosHoje() {
    return await api.get('/api/dashboard/agendamentos-hoje');
  },

  async obterReceitaMensal(mes, ano) {
    return await api.get(`/api/dashboard/receita-mensal?mes=${mes}&ano=${ano}`);
  },

  async obterDespesasMensais(mes, ano) {
    return await api.get(`/api/dashboard/despesas-mensais?mes=${mes}&ano=${ano}`);
  }
};

export const relatorioService = {
  async agendamentosPorPeriodo(dataInicio, dataFim) {
    return await api.get(`/api/relatorios/agendamentos?inicio=${dataInicio}&fim=${dataFim}`);
  },

  async despesasPorPeriodo(dataInicio, dataFim) {
    return await api.get(`/api/relatorios/despesas?inicio=${dataInicio}&fim=${dataFim}`);
  },

  async receitaPorPeriodo(dataInicio, dataFim) {
    return await api.get(`/api/relatorios/receita?inicio=${dataInicio}&fim=${dataFim}`);
  },

  async exportarPDF(tipo, filtros) {
    const response = await api.post(`/api/relatorios/exportar/${tipo}`, filtros, {
      responseType: 'blob'
    });
    
    // Criar URL para download
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio_${tipo}_${new Date().toISOString().split('T')[0]}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

// =====================================================
// SERVIÇOS DE LOGS
// =====================================================
export const logService = {
  async listar(filtros = {}) {
    const params = new URLSearchParams();
    
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    return await api.get(`/api/logs?${params.toString()}`);
  },

  async buscarPorId(id) {
    return await api.get(`/api/logs/${id}`);
  }
};

// =====================================================
// SERVIÇOS DE WHATSAPP
// =====================================================
export const whatsappService = {
  async enviarMensagem(numero, mensagem) {
    try {
      // Simular envio de mensagem via WhatsApp
      // Em produção, isso seria integrado com API do WhatsApp Business
      console.log(` Enviando mensagem para ${numero}:`, mensagem);
      
      // Simular delay de envio
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return {
        success: true,
        message: 'Mensagem enviada com sucesso',
        data: {
          numero,
          mensagem,
          timestamp: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Erro ao enviar mensagem WhatsApp:', error);
      throw new Error('Falha ao enviar mensagem via WhatsApp');
    }
  },

  async verificarStatus(numero) {
    try {
      // Verificar se o número está disponível no WhatsApp
      console.log(` Verificando status do número ${numero}`);
      
      return {
        success: true,
        data: {
          numero,
          disponivel: true,
          verificado: true
        }
      };
    } catch (error) {
      console.error('Erro ao verificar status WhatsApp:', error);
      throw new Error('Falha ao verificar status do número');
    }
  }
};

// =====================================================
// SERVIÇOS DE RECEBIMENTOS
// =====================================================
export const recebimentoService = {
  async listar() {
    return await api.get('/api/recebimentos');
  },

  async buscarPorId(id) {
    return await api.get(`/api/recebimentos/${id}`);
  },

  async buscarPorUsuario(userId) {
    return await api.get(`/api/recebimentos/usuario/${userId}`);
  },

  async buscarPorCliente(clienteId) {
    return await api.get(`/api/recebimentos/cliente/${clienteId}`);
  },

  async criar(dados) {
    return await api.post('/api/recebimentos', dados);
  },

  async atualizar(id, dados) {
    return await api.put(`/api/recebimentos/${id}`, dados);
  },

  async excluir(id) {
    return await api.delete(`/api/recebimentos/${id}`);
  },

  // Métodos para geração de mensalidades
  async buscarClientesComValores(idUsuario) {
    const response = await api.get(`/api/recebimentos/clientes-valores/${idUsuario}`);
    return response.data;
  },

  async buscarRecebimentosPorMes(mes, ano, idUsuario) {
    const response = await api.get(`/api/recebimentos/mes/${mes}/${ano}/${idUsuario}`);
    return response.data;
  },

  async deletarRecebimentosMes(mes, ano, idUsuario) {
    const response = await api.delete(`/api/recebimentos/mes/${mes}/${ano}/${idUsuario}`);
    return response.data;
  },

  async criarRecebimentosMultiplos(recebimentos, idUsuario) {
    const response = await api.post('/api/recebimentos/multiplos', { recebimentos, id_usuario: idUsuario });
    return response.data;
  }
};

// =====================================================
// SERVIÇOS DE NOTIFICAÇÕES
// =====================================================
export const notificacaoService = {
  async listar() {
    return await api.get('/api/notificacoes');
  },

  async listarPorUsuario(id_usuario) {
    return await api.get(`/api/notificacoes/usuario/${id_usuario}`);
  },

  async contarNaoLidas(id_usuario) {
    return await api.get(`/api/notificacoes/usuario/${id_usuario}/nao-lidas`);
  },

  async buscarPorId(id) {
    return await api.get(`/api/notificacoes/${id}`);
  },

  async criar(dados) {
    return await api.post('/api/notificacoes', dados);
  },

  async marcarComoLida(id) {
    return await api.put(`/api/notificacoes/${id}/marcar-lida`);
  },

  async excluir(id) {
    return await api.delete(`/api/notificacoes/${id}`);
  }
};

// =====================================================
// UTILITÁRIOS
// =====================================================
export const utilService = {
  async testarConexao() {
    return await api.get('/api/health');
  },

  async backup() {
    return await api.post('/api/utils/backup');
  },

  async importarDados(arquivo) {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    
    return await api.post('/api/utils/importar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
};

// =====================================================
// SERVIÇO DE CONTATO (Landing Page)
// =====================================================
export const contatoService = {
  async enviar({ nome, email, celular, mensagem }) {
    return await api.post('/api/contato', { nome, email, celular, mensagem });
  }
};

// =====================================================
// GOOGLE CALENDAR SERVICE
// =====================================================
export const googleCalendarService = {
  // Verificar status da conexão
  async getStatus() {
    return await api.get('/api/google-calendar/status');
  },

  // Iniciar autenticação OAuth
  async startAuth() {
    return await api.get('/api/google-calendar/auth/start');
  },

  // Sincronizar todos os eventos
  async syncAll(params = {}) {
    return await api.post('/api/google-calendar/sync/all', params);
  },

  // Sincronizar um evento específico
  async syncEvent(agendamentoId) {
    return await api.post(`/api/google-calendar/sync/${agendamentoId}`);
  },

  // Desconectar Google Calendar
  async disconnect() {
    return await api.delete('/api/google-calendar/disconnect');
  }
};

// Exportar instância do axios para uso direto se necessário
export default api;